/**
 * PartiesForm - Step 3 of CD Creation Wizard
 *
 * Manages all parties involved in the transaction:
 * - Borrowers (at least one required)
 * - Sellers
 * - Lender
 * - Mortgage Broker
 * - Real Estate Brokers
 * - Settlement Agent
 */

import { Plus } from 'lucide-react';
import type React from 'react';
import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import type { Party, PartyType } from '../../types';
import { PARTY_TYPE_OPTIONS, PartyCard } from './shared';

interface PartiesFormProps {
  parties?: Party[];
  onNext: (data: { parties: Party[] }) => void;
  onBack: () => void;
}

/** Create a new empty party with default values */
function createEmptyParty(partyType: PartyType = 'borrower'): Party {
  return {
    party_type: partyType,
    name: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip_code: '',
    email: '',
    phone: '',
    license_id: '',
    nmls: '',
    is_primary: false,
  };
}

/** Default parties when starting fresh */
function getDefaultParties(): Party[] {
  return [
    { ...createEmptyParty('borrower'), is_primary: true },
    { ...createEmptyParty('seller'), is_primary: true },
  ];
}

/** Group parties by type for display */
function groupPartiesByType(parties: Party[]): Map<PartyType, Party[]> {
  const grouped = new Map<PartyType, Party[]>();
  parties.forEach((party) => {
    const existing = grouped.get(party.party_type) ?? [];
    grouped.set(party.party_type, [...existing, party]);
  });
  return grouped;
}

const PartiesForm: React.FC<PartiesFormProps> = ({ parties: initialParties, onNext, onBack }) => {
  const [parties, setParties] = useState<Party[]>(() =>
    initialParties && initialParties.length > 0 ? initialParties : getDefaultParties(),
  );

  const [errors, setErrors] = useState<Map<number, Partial<Record<keyof Party, string>>>>(
    new Map(),
  );
  const [globalError, setGlobalError] = useState<string | null>(null);

  const handlePartyChange = useCallback((index: number, updatedParty: Party) => {
    setParties((prev) => {
      const newParties = [...prev];
      newParties[index] = updatedParty;

      // If this party is set as primary, ensure no other party of same type is primary
      if (updatedParty.is_primary) {
        newParties.forEach((p, i) => {
          if (i !== index && p.party_type === updatedParty.party_type && p.is_primary) {
            newParties[i] = { ...p, is_primary: false };
          }
        });
      }

      return newParties;
    });

    // Clear errors for this party when it changes
    setErrors((prev) => {
      const newErrors = new Map(prev);
      newErrors.delete(index);
      return newErrors;
    });
    setGlobalError(null);
  }, []);

  const handleDeleteParty = useCallback((index: number) => {
    setParties((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => {
      const newErrors = new Map(prev);
      newErrors.delete(index);
      return newErrors;
    });
  }, []);

  const handleAddParty = useCallback((partyType: PartyType = 'borrower') => {
    const newParty = createEmptyParty(partyType);
    setParties((prev) => [...prev, newParty]);
  }, []);

  const validateForm = (): boolean => {
    const newErrors = new Map<number, Partial<Record<keyof Party, string>>>();
    let isValid = true;

    // Validate each party
    parties.forEach((party, index) => {
      const partyErrors: Partial<Record<keyof Party, string>> = {};

      if (!party.name.trim()) {
        partyErrors.name = 'Name is required';
        isValid = false;
      }

      if (!party.party_type) {
        partyErrors.party_type = 'Party type is required';
        isValid = false;
      }

      // Validate email format if provided
      if (party.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(party.email)) {
        partyErrors.email = 'Invalid email format';
        isValid = false;
      }

      // Validate phone format if provided (basic validation)
      if (party.phone && party.phone.replace(/\D/g, '').length < 10) {
        partyErrors.phone = 'Phone number must be at least 10 digits';
        isValid = false;
      }

      if (Object.keys(partyErrors).length > 0) {
        newErrors.set(index, partyErrors);
      }
    });

    // Check for at least one borrower
    const hasBorrower = parties.some((p) => p.party_type === 'borrower');
    if (!hasBorrower) {
      setGlobalError('At least one borrower is required');
      isValid = false;
    }

    // Check for at least one primary borrower
    const borrowers = parties.filter((p) => p.party_type === 'borrower');
    const hasPrimaryBorrower = borrowers.some((p) => p.is_primary);
    if (borrowers.length > 0 && !hasPrimaryBorrower) {
      // Auto-set first borrower as primary if none selected
      const firstBorrowerIndex = parties.findIndex((p) => p.party_type === 'borrower');
      if (firstBorrowerIndex >= 0) {
        setParties((prev) => {
          const newParties = [...prev];
          const existing = newParties[firstBorrowerIndex];
          if (existing) {
            newParties[firstBorrowerIndex] = {
              ...existing,
              is_primary: true,
            };
          }
          return newParties;
        });
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      // Normalize party data - omit fields that are empty (don't send them at all)
      const normalizedParties: Party[] = parties.map((party) => {
        const cleaned: Party = {
          party_type: party.party_type,
          name: party.name,
          is_primary: party.is_primary ?? false,
        };

        // Only include optional fields if they have values
        if (party.email) cleaned.email = party.email;
        if (party.phone) cleaned.phone = party.phone;
        if (party.license_id) cleaned.license_id = party.license_id;
        if (party.nmls) cleaned.nmls = party.nmls;
        if (party.address_street) cleaned.address_street = party.address_street;
        if (party.address_city) cleaned.address_city = party.address_city;
        if (party.address_state) cleaned.address_state = party.address_state;
        if (party.address_zip_code) cleaned.address_zip_code = party.address_zip_code;
        if (party.id) cleaned.id = party.id;

        return cleaned;
      });
      onNext({ parties: normalizedParties });
    }
  };

  // Group parties for display
  const groupedParties = groupPartiesByType(parties);

  // Determine which party types are not yet added
  const existingTypes = new Set(parties.map((p) => p.party_type));
  const suggestedTypes: PartyType[] = ['lender', 'settlement_agent'];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Global Error */}
      {globalError && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded">
          {globalError}
        </div>
      )}

      {/* Instructions */}
      <div className="bg-primary/10 border border-primary/30 text-primary px-4 py-3 rounded">
        <p className="text-sm">
          Add all parties involved in this transaction. At minimum, one borrower is required. You
          can add additional borrowers, sellers, and professional parties as needed.
        </p>
      </div>

      {/* Party Cards */}
      <div className="space-y-4">
        {parties.map((party, index) => (
          <PartyCard
            key={`party-${index}`}
            party={party}
            index={index}
            onChange={(p) => handlePartyChange(index, p)}
            onDelete={() => handleDeleteParty(index)}
            canDelete={
              // Can't delete if it's the only borrower
              !(party.party_type === 'borrower' && groupedParties.get('borrower')?.length === 1)
            }
            errors={errors.get(index)}
            defaultExpanded={!party.name}
          />
        ))}
      </div>

      {/* Add Party Buttons */}
      <div className="border-t pt-4">
        <p className="text-sm font-medium text-foreground mb-3">Add Party</p>
        <div className="flex flex-wrap gap-2">
          {/* Common party types */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleAddParty('borrower')}
          >
            <Plus className="h-4 w-4 mr-1" />
            Borrower
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleAddParty('seller')}
          >
            <Plus className="h-4 w-4 mr-1" />
            Seller
          </Button>

          {/* Suggested party types (if not already added) */}
          {suggestedTypes.map(
            (type) =>
              !existingTypes.has(type) && (
                <Button
                  key={type}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddParty(type)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {PARTY_TYPE_OPTIONS.find((opt) => opt.value === type)?.label ?? type}
                </Button>
              ),
          )}

          {/* Dropdown for other party types */}
          <div className="relative">
            <select
              className="h-9 px-3 text-sm border rounded-md bg-background cursor-pointer hover:bg-accent"
              onChange={(e) => {
                if (e.target.value) {
                  handleAddParty(e.target.value as PartyType);
                  e.target.value = '';
                }
              }}
              defaultValue=""
            >
              <option value="" disabled>
                + Other Party...
              </option>
              {PARTY_TYPE_OPTIONS.filter(
                (opt) => !['borrower', 'seller', 'lender', 'settlement_agent'].includes(opt.value),
              ).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Party Summary */}
      <div className="bg-muted rounded-lg p-4">
        <h4 className="text-sm font-medium text-foreground mb-2">Party Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Borrowers:</span>{' '}
            <span className="font-medium">{groupedParties.get('borrower')?.length ?? 0}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Sellers:</span>{' '}
            <span className="font-medium">{groupedParties.get('seller')?.length ?? 0}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Lender:</span>{' '}
            <span className="font-medium">
              {groupedParties.get('lender')?.length ? 'Yes' : 'No'}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Total Parties:</span>{' '}
            <span className="font-medium">{parties.length}</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit">Next: Closing Costs</Button>
      </div>
    </form>
  );
};

export default PartiesForm;
